import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { IconButton, SvgIconProps, useTheme } from "@mui/material";
import React from "react";

import { TextFieldVariant } from "../types";

interface EndAdornmentProps {
  variant: TextFieldVariant;
  value: string;
  disabled: boolean;
  handleClear: (e: React.MouseEvent<HTMLButtonElement>) => void;
  menuOpen: boolean;
  cursor?: string;
  inputBaseColor: string;
  inputOverlayColor: string;

  /**
   * Optional icon component to display at the end of the input.
   */
  endIcon?: React.ComponentType<SvgIconProps>;
  onEndIconClick?: () => void;
}

export function EndAdornment({
  variant,
  value,
  disabled,
  handleClear,
  menuOpen,
  cursor,
  inputBaseColor,
  inputOverlayColor,
  endIcon: EndIconComponent,
  onEndIconClick,
}: EndAdornmentProps): JSX.Element | null {
  const theme = useTheme();

  if (variant === "search" && value) {
    return (
      <IconButton
        aria-label="clear input"
        onClick={handleClear}
        edge="end"
        size="small"
        disabled={disabled}
        sx={{ mr: "-5px" }}
      >
        <HighlightOffIcon
          data-testid="end-icon"
          fontSize="small"
          sx={{ color: inputBaseColor, width: "24px", height: "24px" }}
        />
      </IconButton>
    );
  }

  if (variant === "select") {
    return (
      <KeyboardArrowDownIcon
        data-testid="end-icon"
        fontSize="small"
        sx={{
          transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
          width: "24px",
          height: "24px",
          cursor,
          color: inputBaseColor,
        }}
      />
    );
  }

  if (EndIconComponent) {
    const endIcon = (
      <EndIconComponent
        data-testid="end-icon"
        fontSize="small"
        sx={{
          color: inputOverlayColor,
          transition: theme.transitions.create(["color"]),
          width: "24px",
          height: "24px",
        }}
      />
    );

    if (!onEndIconClick) {
      return endIcon;
    }

    return (
      <IconButton
        sx={{ mr: "-5px" }}
        edge="end"
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onEndIconClick?.();
        }}
      >
        {endIcon}
      </IconButton>
    );
  }

  return null;
}
